import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { trigger, style, state, animate, transition } from '@angular/animations';
import { ITimeSpan, IRoomScheduleInfo, IDisplayItem } from './room-allocation-table.component';
import { StringHelper } from '../../../../shared/utils/string-helper';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';
import { TranslateService } from '@ngx-translate/core';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { OccupancyType } from 'src/app/shared/models/occupancy';
import { ResourceTableRow } from 'src/app/shared/models/resource-table-row';

/**
 * Represents the calendar view for one single day
 *
 * @export
 * @class RoomAllocationMobileComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-room-allocation-mobile',
  templateUrl: './room-allocation-mobile.component.html',
  styleUrls: ['./room-allocation-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapsed', [
      state('true', style({
        height: '0'
      })),
      state('false', style({
        height: '*'
      })),
      transition('* => false', [
        style({ height: 0 }),
        animate(250, style({ height: '*' }))
      ]),
      transition('* => true', [
        style({ height: '*' }),
        animate(250, style({ height: 0 }))
      ]
      )
    ])
  ]
})
export class RoomAllocationMobileComponent implements OnInit {

  @Input() item: IDisplayItem;

  @Input() secondaryText: string;

  @Input() bgLight: boolean;

  @Input() hideInfoButton: boolean;

  @Input() hideCalendarButton: boolean;

  /**
   * Date of the displayed day
   *
   * @type {Date}
   * @memberof RoomAllocationMobileComponent
   */
  @Input() baseDate: Date;

  /**
   * Type of items to display (room or device)
   *
   * @type {ResourceType}
   * @memberof RoomAllocationMobileComponent
   */
  @Input() type: ResourceType;

  /**
   * Info button label
   *
   * @type {string}
   * @memberof RoomAllocationMobileComponent
   */
  @Input() openResourceInfoText: string;

  /**
   * Calendar button label
   *
   * @type {string}
   * @memberof RoomAllocationMobileComponent
   */
  @Input() openResourceCalendarText: string;

  /**
   * Defines the component presentation style
   */
  @Input() useReservationMode = false;

  /**
   * Defines which data to display
   */
  @Input() useColumnsDataAsCaption = false;

  /**
   * Event raised on click on "open room info"
   *
   * @memberof RoomAllocationMobileComponent
   */
  @Output() openRoomInfo = new EventEmitter<number>();

  /**
   * Event raised on click on "open roomschedule"
   *
   * @memberof RoomAllocationMobileComponent
   */
  @Output() openSchedule = new EventEmitter<IRoomScheduleInfo>();

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  collapsed = true;

  renderContent = false;

  displayTimeSpanStart = 6;

  displayTimeSpanEnd = 22;

  constructor(private translate: TranslateService) { }

  ngOnInit() {

    if (this.item === undefined) {
      throw new Error('Please provide property ' + nameof<RoomAllocationMobileComponent>('item'));
    }

    if (this.type === undefined) {
      throw new Error('Please provide property ' + nameof<RoomAllocationMobileComponent>('type'));
    }
  }

  /**
    * format time (eg. 15:00)
    *
    * @param {number} hour
    * @param {number} min
    * @returns
    * @memberof RoomAllocationComponent
    */
  getTimeString(hour: number, min: number = 0): string {
    return StringHelper.formatTime(hour, min);
  }

  /**
    * format timespan (eg. 13:00 -  17:45)
    *
    * @param {ITimeSpan} span
    * @returns
    * @memberof RoomAllocationComponent
    */
  getTimeSpanString(span: ITimeSpan): string {
    return StringHelper.formatTimeSpan(span.fromHour, span.fromMin, span.toHour, span.toMin);
  }

  /**
 * returns required block-height in %
 *
 * @param {ITimeSpan} span
 * @returns {string}
 * @memberof RoomAllocationComponent
 */
  getBlockHeight(span: ITimeSpan): string {
    const minutes = (span.toHour * 60 + span.toMin) - (span.fromHour * 60 + span.fromMin);
    const hours = minutes / 60;
    return Math.round(hours * 100) + '%';
  }

  /**
   * return required block top-space in %
   *
   * @param {ITimeSpan} span
   * @returns {string}
   * @memberof RoomAllocationComponent
   */
  getBlockTop(span: ITimeSpan): string {
    return Math.round(span.fromMin / 60 * 100) + '%';
  }

  onOpenSchedule() {
    this.openSchedule.emit({
      roomId: this.item.resourceId,
      designation: this.item.designation,
      hasWarnings: Boolean(this.item.warning),
      isBookable: this.item.isBookable
    });
  }

  onOpenRoomInfo() {
    this.openRoomInfo.emit(this.item.resourceId);
  }

  onCreateReservation(params: ICreateReservationParams) {
    this.createReservation.emit(params);
  }

  onRequestCreateReservation(params: IDisplayItem) {
    const aVacantSlot = params.scheduleItems.find(item => item.type === OccupancyType.Reservable);
    // REVIEW: may require to hide button and display warn icon
    if (!aVacantSlot) { return; }

    this.createReservation.emit({
      idReservation: null,
      idRessource: params.resourceId,
      from: new Date(aVacantSlot.from),
      to: new Date(aVacantSlot.to),
    });

  }

  toFlagSet(boolList: boolean[]) {
    return boolList.map(li => Boolean(li) ? '1' : '0').join('');
  }

  getRowCaption(item: IDisplayItem) {
    return this.useColumnsDataAsCaption === true
      ? item.additionalColumsData.filter(ac => ac.showFromScreenSize === 1).map(ac => ac.fieldValue).join(' - ')
      : item.designation;
  }

  getSubColumns(additionalColumns: ResourceTableRow[]) {
    return additionalColumns.filter(ac => ac.showFromScreenSize > 1);
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  onCollapsedStarted() {
    if (!this.collapsed) {
      this.renderContent = true; // insert content before open
    }
  }

  onCollapsedDone() {
    if (this.collapsed) {
      this.renderContent = false; // remove content after close
    }
  }

  zebra(index: number): string {
    return index % 2 === 0 ? 'bg-light' : '';
  }

  warningClass(): string {
    return this.item.isBookable ? 'text-warning' : 'text-danger';
  }

}
